import { Brand10, Brand60, Brand90, Neutral100 } from '@src/utils/colors';
import {
  JourneyMilestoneResult,
  JourneyModel,
  MilestoneStatus,
  MilestoneStatusType
} from './journey-mapper-constants';

/**
 * Plain-language client guide renderer, ported from the real
 * journey_guide.py (data-continuiti). Produces the same branded HTML
 * document the source tool renders to PDF via Playwright (guide.pdf) —
 * intended to be handed to the backend PDF-export endpoint verbatim, and
 * previewable in-app via an iframe (see ClientGuideView.tsx).
 */

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** Plain-language explainers by stage number. Stage names vary by template; these describe the universal idea. */
const STAGE_GUIDE: Record<number, [what: string, why: string]> = {
  1: [
    'This is how people first find out you exist — an ad, a search, a recommendation, a social post. Nobody buys here; they simply become aware of you.',
    "If this stage is weak, everything after it is starved. And if it isn't measured, you can't tell which marketing dollars actually bring people in the door."
  ],
  2: [
    "Now they're interested but not convinced. They compare options, read reviews, ask questions, and quietly decide whether to trust you.",
    'Most people leave at this stage. Every unanswered question or missing trust signal here quietly costs sales you never see.'
  ],
  3: [
    'The moment of action — the purchase, booking, signup, or inquiry. Everything before this stage exists to make this moment happen.',
    "This is where revenue is won or lost. Small improvements here are worth more than large improvements anywhere else, and accurate measurement here is what tells your marketing what's working."
  ],
  4: [
    "What happens right after the 'yes' — confirmation, delivery, onboarding, first use. The customer is paying close attention to whether you deliver on the promise.",
    'A great experience here is the difference between a one-time transaction and a customer. It’s also the cheapest moment to earn a review or a referral.'
  ],
  5: [
    'The return trip — coming back to buy again, renewing, referring friends, leaving reviews, joining your loyalty program.',
    'Repeat customers cost a fraction of new ones and spend more. Brands that win long-term win here, not at the top of the funnel.'
  ]
};

/** Foundation capabilities in plain words: [what it is, why it matters]. */
const FOUNDATION_GUIDE: Record<string, [what: string, why: string]> = {
  analytics: [
    'Counts and understands website visits',
    "Without it, everything else is guesswork — it's the scoreboard."
  ],
  ux_analytics: [
    'Shows how people actually use the site (anonymously)',
    "Reveals where visitors struggle or give up, so fixes aren't guesses."
  ],
  tag_mgmt: [
    'The control panel for all measurement tools',
    'Lets tools be added, tested, and governed without rebuilding the site.'
  ],
  sst: [
    'A sturdier, more private way of sending measurement data',
    'Browsers and ad-blockers increasingly hide data; this recovers it.'
  ],
  cdp: [
    'Connects the dots so one customer looks like one person',
    'Without it, the ad clicker, email opener, and loyalty member look like three strangers.'
  ],
  consent: [
    'Asks visitors for permission and honors their choices',
    'Keeps marketing compliant with privacy laws — and keeps trust intact.'
  ],
  consent_mode: [
    'Passes those permission choices along to advertising platforms',
    'Ad platforms now require this; without it, reported results undercount and audience features shut off in some regions.'
  ],
  identity: [
    'Puts your own customer list to work in your advertising',
    'As cookies fade, the customer data you already own is what keeps ads finding the right people.'
  ],
  attribution: [
    'An independent scorekeeper for which marketing actually drove sales',
    'Each ad platform grades its own homework; this is the referee.'
  ],
  pixels: [
    'Lets advertising platforms see what their ads led to',
    "Without pixels, ad platforms can't learn what works — every campaign starts from zero."
  ]
};

const GROUP_GUIDE: Record<string, string> = {
  site: "The website's own plumbing — how visits are counted, tools are managed, and permission is asked.",
  media:
    'The advertising side’s plumbing — how campaigns learn, target, and prove what they delivered.'
};

/** Plain-language "why we use this data point," matched against the milestone's signal (name as fallback). First match wins. */
const SIGNAL_WHY: [RegExp, string][] = [
  [
    /source\/medium|session start|utm/i,
    'every visit gets stamped with where it came from, so credit for the sale lands on the channel that earned it'
  ],
  [
    /impression|ad click|reach/i,
    "it's the earliest breadcrumb in the journey — the moment marketing first touched this person"
  ],
  [
    /view_item|product detail|pdp/i,
    'it shows which products pull attention before any money changes hands — demand you can see early'
  ],
  [
    /add_to_cart|begin_checkout|checkout/i,
    "it's the strongest signal of buying intent short of the purchase itself — and where drop-off analysis starts"
  ],
  [
    /purchase|transaction|order placed|\border\b/i,
    "it's the ground truth — the event every channel, campaign, and dollar ultimately gets judged against"
  ],
  [
    /sign.?up|email capture|sms capture|subscribe|opt.in|list join/i,
    "it converts an anonymous visitor into someone you can reach again for free — the cheapest audience you'll ever build"
  ],
  [
    /generate_lead|form_start|form submit|lead form|inquiry/i,
    "it's the moment a visitor raises their hand — the handoff point between marketing and sales"
  ],
  [
    /call|dynamic number|dni/i,
    'phone calls carry the highest intent of any inquiry; a tracked number ties each call back to the exact ad or page that caused it'
  ],
  [
    /review submitted|review request|ugc/i,
    'reviews are trust you can reuse — each one measurably lifts conversion for the next shopper'
  ],
  [
    /subscription_start|rebill|recurring|dunning|renewal/i,
    "recurring revenue is won or lost in these events — they're the heartbeat of subscriber health"
  ],
  [
    /delivery|shipment|fulfillment|tracking/i,
    'delivery confirmation starts the clock on the moments that follow — the review ask, the reorder, the second sale'
  ],
  [
    /loyalty|points|tier|redemption/i,
    'it identifies your best customers by behavior, not guesswork — the group most worth protecting'
  ],
  [
    /referral|advocate|ambassador/i,
    'referred customers convert better and stay longer — this tells you which customers are actively growing your business for you'
  ],
  [
    /quiz|survey|nps|assessment/i,
    "it's the customer telling you directly what analytics can only infer — the 'why' behind the numbers"
  ],
  [
    /appointment|booking|consultation|scheduled/i,
    'for this business the booking IS the conversion — this is its receipt'
  ],
  [
    /crm|closed.won|opportunity|pipeline|offline/i,
    'it connects marketing to actual revenue in the sales system, not just to leads that may go nowhere'
  ],
  [
    /app install|first open|in.app/i,
    'it marks the shift to your highest-engagement channel — customers in the app buy more, more often'
  ],
  [
    /engaged session|scroll|page depth|content|engagement/i,
    'it separates genuine interest from a bounce — the difference between traffic and an audience'
  ],
  [
    /account created|user_id|login/i,
    'a logged-in customer is recognizable across devices and time — the foundation for everything personalized'
  ]
];

const signalWhy = (signal: string, name = ''): string => {
  const text = `${signal} | ${name}`.toLowerCase();
  const match = SIGNAL_WHY.find(([pattern]) => pattern.test(text));
  return match
    ? match[1]
    : "it's the clearest observable moment that this step actually happened — without it, this part of the journey is a guess";
};

const STATUS_PLAIN: Record<MilestoneStatusType, [mark: string, label: string, color: string]> = {
  covered: ['✓', 'In place', '#1F7A4D'],
  gap: ['△', 'Not in place yet', '#C4442A'],
  discovery: ['○', 'Happens off the website', '#5F5B7A']
};

const statusSentence = (m: JourneyMilestoneResult): string => {
  if (m.status === MilestoneStatus.COVERED) return `Handled today by ${m.tools.join(', ')}.`;
  if (m.status === MilestoneStatus.GAP) {
    return 'None of the current tools handles this step yet — an opportunity worth a conversation.';
  }
  return "This step happens in person, on calls, or in back-office systems rather than on the website — we'll confirm together how it's tracked.";
};

/** Renders the branded, client-facing guide HTML document (same template used for guide.pdf). */
export const renderGuideHtml = (journey: JourneyModel): string => {
  const who = journey.clientName || journey.domain || 'your business';
  const sc = journey.score;
  const fnd = journey.foundation;
  const fp = fnd.filter(f => f.present).length;

  const stagesHtml = journey.stages
    .map(s => {
      const [what, why] = STAGE_GUIDE[s.num] ?? ['', ''];
      const msHtml = s.milestones
        .map(m => {
          const [mark, label, color] = STATUS_PLAIN[m.status];
          const purpose = m.purpose ?? '';
          const sig = m.signal ?? '';
          const why2 = signalWhy(sig, m.name);
          const sigHtml = `<p class="mssig"><b>How we track it:</b> ${
            sig ? `<code>${escapeHtml(sig)}</code> — ` : ''
          }${escapeHtml(why2)}.</p>`;
          return `
            <div class="ms">
              <div class="msname"><span class="mk" style="color:${color}">${mark}</span>
                ${escapeHtml(m.name)} <span class="mstat" style="color:${color}">${label}</span></div>
              ${purpose ? `<p class="mspur">${escapeHtml(purpose)}</p>` : ''}
              ${sigHtml}
              <p class="msstat">${escapeHtml(statusSentence(m))}</p>
            </div>`;
        })
        .join('');
      return `
        <div class="stage">
          <h2><span class="snum">${s.num}</span>${escapeHtml(s.name)}</h2>
          <p class="swhat">${escapeHtml(what)}</p>
          <p class="swhy"><b>Why it matters:</b> ${escapeHtml(why)}</p>
          ${msHtml}
        </div>`;
    })
    .join('');

  const foundHtml = Object.entries(journey.foundationGroups)
    .map(([gkey, glabel]) => {
      const items = fnd.filter(f => f.group === gkey);
      if (items.length === 0) return '';
      const header =
        `<h3 style="font:800 12pt 'Barlow Condensed';color:${Brand90};text-transform:uppercase;margin:10pt 0 3pt">${escapeHtml(glabel)}</h3>` +
        `<p class='msstat' style='margin-left:0'>${escapeHtml(GROUP_GUIDE[gkey] ?? '')}</p>`;
      const itemsHtml = items
        .map(f => {
          const [what, why] = FOUNDATION_GUIDE[f.cap] ?? [f.label, ''];
          const mark = f.present ? '✓' : '—';
          const color = f.present ? '#1F7A4D' : '#5F5B7A';
          const status = f.present ? `In place: ${f.tools.join(', ')}` : 'Not in place yet';
          return `
        <div class="ms">
          <div class="msname"><span class="mk" style="color:${color}">${mark}</span>
            ${escapeHtml(what)} <span class="mstat" style="color:${color}">${escapeHtml(status)}</span></div>
          <p class="msstat">${escapeHtml(why)}</p>
        </div>`;
        })
        .join('');
      return header + itemsHtml;
    })
    .join('');

  const gaps = journey.stages.flatMap(s =>
    s.milestones.filter(m => m.status === MilestoneStatus.GAP).map(m => m.name)
  );
  const nextHtml =
    gaps.length > 0
      ? `<h2>Where to focus next</h2><p>The steps marked <b style='color:#C4442A'>△ not in place yet</b> are the conversation starters: ${escapeHtml(
          gaps.join(', ')
        )}. Each one is a moment in your customer's journey that currently happens unsupported and unmeasured.</p>`
      : '';

  return `<!doctype html><html><head><meta charset="utf-8">
<style>
@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800&family=Inter:wght@400;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap');
@page { size: Letter; margin: 0.7in 0.75in; }
body { font: 10.5pt/1.55 'Inter', sans-serif; color: #1B1A2E; }
.mark { font: 800 20pt 'Barlow Condensed'; color: ${Brand90}; text-transform: uppercase; letter-spacing: .02em; }
.mark b { color: ${Brand60}; }
.sub { font: 500 9pt 'IBM Plex Mono', monospace; color: #5F5B7A; margin: 2pt 0 14pt; }
h1 { font: 800 24pt 'Barlow Condensed'; color: ${Brand90}; text-transform: uppercase; margin: 4pt 0 8pt; }
h2 { font: 800 15pt 'Barlow Condensed'; color: ${Brand90}; text-transform: uppercase;
     border-bottom: 2pt solid ${Brand90}; padding-bottom: 3pt; margin: 18pt 0 8pt; }
.snum { display:inline-block; background:${Brand90}; color:${Neutral100}; border-radius:50%;
        width:16pt; height:16pt; text-align:center; font-size:11pt; line-height:16pt;
        margin-right:6pt; vertical-align: 2pt; }
p { margin: 0 0 7pt; }
.lede { font-size: 11.5pt; }
.swhat { color:#1B1A2E; }
.swhy { color:#5F5B7A; margin-bottom: 10pt; }
.legend { background:${Brand10}; border-radius:8pt; padding:10pt 12pt; margin: 8pt 0 4pt; }
.legend p { margin: 0 0 4pt; font-size: 9.5pt; }
.ms { margin: 0 0 9pt 6pt; page-break-inside: avoid; }
.msname { font-weight: 700; font-size: 10.5pt; }
.mk { font-family:'IBM Plex Mono', monospace; margin-right: 3pt; }
.mstat { font: 600 8pt 'IBM Plex Mono', monospace; text-transform: uppercase;
         letter-spacing:.04em; margin-left: 5pt; }
.mspur { margin: 1pt 0 1pt 14pt; color:#1B1A2E; }
.mssig { margin: 0 0 1pt 14pt; color:#3E3A5C; font-size: 9.5pt; }
.mssig code { font: 600 8.5pt 'IBM Plex Mono', monospace; background:${Brand10};
              border-radius:3pt; padding: 0.5pt 3pt; color:${Brand90}; }
.mssig b { font-size: 8pt; text-transform: uppercase; letter-spacing:.04em;
           color:#5F5B7A; font-family:'IBM Plex Mono', monospace; }
.msstat { margin: 0 0 0 14pt; color:#5F5B7A; font-size: 9.5pt; }
.snapshot { background:${Brand10}; border-left: 4pt solid ${Brand60}; border-radius: 6pt;
            padding: 9pt 12pt; margin: 6pt 0 10pt; }
.foot { font-size:8pt; color:#5F5B7A; border-top:1pt solid #D9D5EE;
        margin-top:16pt; padding-top:6pt; }
</style></head><body>
<div class="mark">DATA CON<b>TINUITI</b></div>
<div class="sub">customer journey guide &middot; prepared for ${escapeHtml(who)} &middot;
${escapeHtml(journey.clientTypeLabel)} &middot; ${escapeHtml(journey.verticalLabel)}</div>

<h1>Your customer's journey, step by step</h1>
<p class="lede">Every customer travels a path — from first hearing about ${escapeHtml(who)}
to buying, coming back, and telling others. This guide lays out that path in
${journey.stages.length} stages, explains why each step matters, and shows
where your current tools support the journey and where they don't.</p>

<div class="legend">
<p><b style="color:#1F7A4D">✓ In place</b> — your current tools already support and measure this step.</p>
<p><b style="color:#C4442A">△ Not in place yet</b> — nothing in your current toolkit handles this step; an opportunity.</p>
<p><b style="color:#5F5B7A">○ Happens off the website</b> — this step lives in person, on calls, or in back-office systems; we'll confirm it together.</p>
</div>

<div class="snapshot"><b>Where things stand:</b> of the steps we can verify from
your website, ${sc.covered} of ${sc.detectable} are supported
by your current tools (${sc.pct}%), and ${fp} of ${fnd.length} foundation
capabilities are in place.</div>

${stagesHtml}

<h2>The foundations underneath it all</h2>
<p>These aren't steps in the journey — they're the plumbing that every step
depends on.</p>
${foundHtml}

${nextHtml}

<div class="foot">Prepared with Data Continuiti, a Tinuiti tool. "In place" reflects
what's visible from your public website; systems that work behind the scenes
(like a CRM without a website connection) are marked for confirmation rather
than assumed missing.</div>
</body></html>`;
};
